import { batch } from "solid-js";
import { reconcile } from "solid-js/store";

import { CreditLineCanada } from "@repo/kikoff-models/lending/CreditLine";
import Subscription, {
  SubscriptionCanada,
  SubscriptionUsa,
} from "@repo/kikoff-models/Subscription";
import canadaRpc from "@repo/protobuf/api/canadaRpc";
import { webRpc } from "@repo/protobuf/api/rpc";
import { SubscriptionPlan as UsSubscriptionPlanPb } from "@repo/protobuf/gen/web/public/subscription_pb";
import FeatureStore from "@repo/utils-solid/FeatureStore";

import { track } from "~/analytics/client";

import { useCreditLines } from "./creditLine";

const initialState = {
  byId: {} as Record<Subscription.Id, Subscription>,
  all: [] as Subscription.Id[],
  active: null as Subscription.Id | null,
  planById: {} as Record<Subscription.Plan.Id, Subscription.Plan>,
  allPlans: [] as Subscription.Plan.Id[],
};

export const { Provider, useSubscriptions, Context } = FeatureStore.init(
  "Subscriptions",
  initialState,
  ([store, setStore], { attachAction, withLogger }) => {
    const [, setCreditLines] = useCreditLines();

    const accessors = {
      active: attachAction(
        (): Subscription.Resolved | undefined => {
          if (!store.active) return;
          const active = store.byId[store.active]!;
          return { ...active, plan: store.planById[active.planId]! };
        },
        () => actions.fetch()
      ),
      all: attachAction(
        () => store.all.map((id) => store.byId[id]),
        () => actions.fetchPlans(),
        () => (res) => res.length > 0
      ),
      plan: (id: Subscription.Plan.Id) => store.planById[id],
      plansByTier: attachAction(
        (tier: Subscription.Tier) =>
          store.allPlans
            .map((plan) => store.planById[plan]!)
            .filter((plan) => plan.tier === tier),
        () => actions.fetchPlans(),
        () => (res) => res.length > 0
      ),
      eligiblePlans: attachAction(
        () => {
          const activeSubscription = store.byId[store.active!];
          return store.allPlans
            .map((plan) => store.planById[plan]!)
            .filter(
              (plan) =>
                plan.tier !==
                store.planById[activeSubscription?.planId ?? ""]?.tier
            );
        },
        () => {
          actions.fetchPlans();
          actions.fetch();
        },
        () => (res) => res.length > 0
      ),
      allPlans: attachAction(
        () => store.allPlans.map((plan) => store.planById[plan]!),
        () => actions.fetchPlans(),
        () => (res) => res.length > 0
      ),
    };

    const mutations = {
      one(subscription: Subscription) {
        setStore("byId", subscription.id, reconcile(subscription));
      },
      many(subscriptions: Subscription[]) {
        batch(() => {
          for (const subscription of subscriptions) {
            mutations.one(subscription);
          }
        });
      },
      updateAll(subscriptions: Subscription[]) {
        mutations.many(subscriptions);
        setStore(
          "all",
          subscriptions.map(({ id }) => id)
        );
      },

      onePlan(plan: Subscription.Plan) {
        setStore("planById", plan.id, reconcile(plan));
      },
      manyPlans(plans: Subscription.Plan[]) {
        batch(() => {
          for (const plan of plans) {
            mutations.onePlan(plan);
          }
        });
      },
      updatePlans(plans: Subscription.Plan[]) {
        mutations.manyPlans(plans);
        setStore(
          "allPlans",
          plans.map(({ id }) => id)
        );
      },
    };

    const us = {
      tier: {
        byProtoEnum: {
          [UsSubscriptionPlanPb.BASIC]: "basic",
          [UsSubscriptionPlanPb.PREMIUM]: "premium",
          [UsSubscriptionPlanPb.ULTIMATE]: "ultimate",
        } satisfies Partial<Record<UsSubscriptionPlanPb, Subscription.Tier>>,
        name: {
          basic: "Kikoff Credit Service",
          premium: "Kikoff Credit Service Premium",
          ultimate: "Kikoff Credit Service Ultimate",
        } satisfies Record<Subscription.Tier, string>,
      },
      actions: {
        fetchPlans() {
          return webRpc.Subscription.listSubscriptionPlans({})
            .SUCCESS((data) => {
              mutations.updatePlans(
                data.subscriptionPlans.map(SubscriptionUsa.Plan.normalize)
              );
            })
            .elseThrow("Failed to fetch subscription plans.");
        },
        // TODO
        fetch() {},
        start() {},
        changePlan() {},
      },
    };

    const canada = {
      actions: {
        fetchPlans() {
          return canadaRpc.Subscription.listSubscriptionPlans({}).then(
            ({ plans }) => {
              mutations.updatePlans(
                plans.map(SubscriptionCanada.Plan.normalize)
              );
            }
          );
        },
        fetch() {
          return canadaRpc.Subscription.listUserSubscriptions({}).then(
            ({ userSubscriptions }) => {
              batch(() => {
                mutations.manyPlans(
                  userSubscriptions.map(({ plan }) =>
                    SubscriptionCanada.Plan.normalize(plan!)
                  )
                );
                mutations.updateAll(
                  userSubscriptions.map(SubscriptionCanada.normalize)
                );

                setStore(
                  "active",
                  userSubscriptions.find((s) => s.active)?.id || null
                );
              });
            }
          );
        },
        start(planId: Subscription.Plan.Id) {
          return canadaRpc.Subscription.openSubscription({ planId }).then(
            () => {
              actions.fetch();
            }
          );
        },
        changePlan(planId: Subscription.Plan.Id) {
          const from = accessors.active()?.plan.tier!;
          const to = accessors.plan(planId)!.tier;

          track.subscription.changePlan("attempt", { from, to });

          return canadaRpc.Subscription.changeSubscriptionPlan({ planId })
            .then(({ creditLine }) => {
              track.subscription.changePlan("success", { from, to });

              actions.fetch();
              setCreditLines.one(CreditLineCanada.normalize(creditLine!));
            })
            .catch((e) => {
              track.subscription.changePlan("failure", {
                errors: e instanceof Error ? [e.message] : [],
                from,
                to,
              });
              throw e;
            });
        },
      },
    };

    const actions = withLogger(
      "action",
      import.meta.env.API_ADAPTER === "canada" ? canada.actions : us.actions
    );

    return { accessors, actions };
  }
);
